import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import KillSwitchBanner from "@/components/marketing/KillSwitchBanner";
import { OrderInviteHero } from "@/components/marketing/orderInvite/OrderInviteHero";
import { HowItWorksCard } from "@/components/marketing/orderInvite/HowItWorksCard";
import { InviteTextEditors } from "@/components/marketing/orderInvite/InviteTextEditors";
import { InviteBatchTable } from "@/components/marketing/orderInvite/InviteBatchTable";
import { InviteBatchDetailDialog } from "@/components/marketing/orderInvite/InviteBatchDetailDialog";
import { InviteOnOffCard } from "@/components/marketing/orderInvite/InviteOnOffCard";
import { InviteTimingCard } from "@/components/marketing/orderInvite/InviteTimingCard";
import { BacklogSweepCard } from "@/components/marketing/orderInvite/BacklogSweepCard";
import { InviteLiveFeed } from "@/components/marketing/orderInvite/InviteLiveFeed";
import { useMarketingSettings } from "@/hooks/sms/useMarketingSettings";
import { useInviteRotation } from "@/hooks/sms/useInviteRotation";
import { useInviteForecast } from "@/hooks/sms/useInviteForecast";
import { useInviteBatches } from "@/hooks/sms/useInviteBatches";
import { useInviteBatchOutcomes } from "@/hooks/sms/useInviteBatchOutcomes";
import { useQueuedInviteCount } from "@/hooks/sms/useQueuedInviteCount";
import { inviteSegments, formatNextBatch, formatHourLabel } from "@/utils/inviteForecast";
import { DEFAULT_INVITE_MESSAGE_BODY, localDate } from "@/utils/smsShared";
import type { MarketingCampaign } from "@/types/sms";

/**
 * The Invitation tab of /marketing (captain-approved round-4 design): the
 * Order Invite standing play as its own feature page — status header, the Today
 * hero (the "This month" roll-up moved to the Contacts tab), How it works + safety rails, message editors with an iPhone
 * preview, the last-14-days batch history with per-batch drill-down, and the
 * right-rail controls (on/off, timing, backlog sweep, live feed). All the
 * controls that used to live on the Settings "Order Invite" card now live here.
 */
export default function InvitationTab() {
  const { data: settings } = useMarketingSettings();
  const { data: rotation } = useInviteRotation();
  const { data: forecast } = useInviteForecast();
  const { data: batches = [], isLoading: batchesLoading } = useInviteBatches(31);
  const todayLocal = settings ? localDate(new Date(), settings.timezone) : null;
  const todayBatch = (todayLocal && batches.find((b) => b.invite_occurrence_date === todayLocal)) || null;
  const { data: queuedCount = 0, dataUpdatedAt: queuedUpdatedAt } = useQueuedInviteCount(todayBatch?.id ?? null);
  const queryClient = useQueryClient();
  const prevQueuedCount = useRef(0);
  useEffect(() => {
    if (prevQueuedCount.current > 0) queryClient.invalidateQueries({ queryKey: ["sms-invite-batches"] });
    prevQueuedCount.current = queuedCount;
  }, [queuedUpdatedAt, queuedCount, queryClient]);
  const [detailBatch, setDetailBatch] = useState<MarketingCampaign | null>(null);

  const tableBatches = useMemo(() => batches.slice(0, 14), [batches]);
  const tableIds = useMemo(() => tableBatches.map((b) => b.id), [tableBatches]);
  const { data: outcomes = {} } = useInviteBatchOutcomes(tableIds);

  if (!settings || !rotation) {
    return <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>;
  }

  const paused = settings.kill_switch;
  const sendHour = parseInt(rotation.send_time.slice(0, 2), 10) || 0;
  const sendLabel = formatHourLabel(sendHour);
  const followUps = rotation.follow_ups ?? 1;

  const inviteBody = rotation.invite_message_body ?? DEFAULT_INVITE_MESSAGE_BODY;
  const segmentsPerInvite = inviteSegments(inviteBody, {
    storeName: settings.store_name,
    storePhone: settings.store_phone,
    storeAddress: settings.store_address,
  });

  // Both facts when both apply: the emergency stop must not hide that Order
  // Invite itself is switched off.
  const status = paused
    ? {
        dot: "text-status-danger",
        text: rotation.enabled ? "Paused · emergency stop" : "Off · emergency stop on",
        tone: "bg-status-danger/15 text-status-danger",
      }
    : rotation.enabled
      ? {
          dot: "text-status-success",
          text: `Running · next batch ${formatNextBatch(forecast?.next_batch_at ?? null, settings.timezone)}`,
          tone: "bg-status-success/15 text-status-success",
        }
      : { dot: "text-muted-foreground", text: "Off", tone: "bg-muted text-muted-foreground" };

  return (
    <div className="space-y-4">
      <KillSwitchBanner />

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Order Invite</h2>
          <p className="mt-0.5 max-w-prose text-sm text-muted-foreground">
            The standing play that texts recent customers one invite a day to join your text club.
          </p>
        </div>
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${status.tone}`}>
          <span aria-hidden="true" className={status.dot}>
            ●
          </span>
          {status.text}
        </span>
      </header>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <OrderInviteHero
            forecast={forecast}
            settings={settings}
            segmentsPerInvite={segmentsPerInvite}
            paused={paused}
            enabled={rotation.enabled}
            todayBatch={todayBatch}
            queuedCount={queuedCount}
            onOpenBatch={setDetailBatch}
          />
          <HowItWorksCard settings={settings} sendLabel={sendLabel} followUps={followUps} />
          <InviteTextEditors />
          <InviteBatchTable
            batches={tableBatches}
            outcomes={outcomes}
            onOpenDetail={setDetailBatch}
            isLoading={batchesLoading}
          />
        </div>
        <div className="space-y-4">
          <InviteOnOffCard />
          <InviteTimingCard />
          <BacklogSweepCard paused={paused} />
          <InviteLiveFeed batches={tableBatches} />
        </div>
      </div>

      <InviteBatchDetailDialog batch={detailBatch} onOpenChange={(open) => !open && setDetailBatch(null)} />
    </div>
  );
}
